import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';

/**
 * A custom hook that scrolls to the element corresponding to the URL hash
 * after navigation or when the hash changes.
 * 
 * @param {Object} options - Configuration options
 * @param {number} options.delay - Delay in ms before scrolling (default: 100)
 * @param {number} options.offset - Offset in pixels from the top (default: 10)
 * @returns {void}
 */
const useHashScroll = ({ delay = 100, offset = 10 } = {}) => {
  const { hash, pathname } = useLocation();

  useEffect(() => {
    // If there's a hash in the URL
    if (hash) {
      // Remove the # character
      const elementId = hash.replace('#', '');
      
      // Set a timeout to ensure the DOM has been rendered
      const timeoutId = setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          // Calculate position with offset
          const elementPosition = element.getBoundingClientRect().top;
          const offsetPosition = elementPosition + window.pageYOffset - offset;
          
          // Scroll to the element
          window.scrollTo({
            top: offsetPosition,
            behavior: 'smooth'
          });
        }
      }, delay);
      
      return () => clearTimeout(timeoutId);
    } else {
      // If no hash, scroll to top when route changes
      window.scrollTo(0, 0);
    }
  }, [hash, pathname, delay, offset]);
};

export default useHashScroll; 